import React, { MouseEventHandler, useEffect } from "react";
import { AntibodyButton } from "./AntibodyButton";
import { ExperienceButton } from "./ExperienceButton";
import { FreezeButton } from "./FreezeButton";
import { RefreshButton } from "./RefreshButton";

/** @jsx jsx */
import { jsx, css } from '@emotion/react';
import { faArrowAltCircleRight, faArrowCircleLeft, faArrowCircleRight, faChevronDown, faChevronUp, faLock, faLockOpen } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { GameElement } from "../../../shared/gameElementTypes";
import { Colors } from "../palettes/colorPalette";
import { canGroupPromote, findAlikeElements, isRewardCell, Player } from "../../../shared/player";
import { getImmuneCellTier, getRewardCellTier, getSystem } from "../../../shared/utils";
import { GameClient } from "../gameClient";
import { coloredSpritesheet } from "../rendering/spriteFactory";
import { PlacementSystem } from "../systems/placementSystem";
import { GoldCounter } from "./GoldCounter";
import { NextRoundButton } from "./NextRoundButton";
import { Sound } from "../../../shared/sound";
import { TierMarker } from "./TierMarker";
import { backgroundTextureMarket } from "../uiConstants";
import { CapacityPanel } from "./CapacityPanel";
import { CameraZoomLevel, ShopConstants } from "../../../shared/constants";

declare var gameClient: GameClient;

const offerStyle = css`
	position:absolute;
	display: flex;
	flex-direction: column;
	place-content: space-between;
	width: 80%;
	transition: width .1s ease;
	bottom: 1vh;
    left: 50%;
    transform: translate(-50%, 0);
	color: ${Colors.UIMarketTextAndBorders};
	filter: drop-shadow(2px 4px 2px ${Colors.UIMarketShadow});
`;

const tooltipAnchor = css`
	position: absolute;
	bottom: 100%;
	left: 0;
	margin-bottom: 1vh;
	display: flex;
	flex-direction: column;
	gap: 1vh;
	pointer-events: none;
`;

const offerTab = css`
	background: url(${backgroundTextureMarket});
	background-color: ${Colors.UIMarketBackground};
	padding: .5vw;
	width: fit-content;
	display: flex;
	border-top-left-radius: .5vw;
	border-top-right-radius: .5vw;
	gap: .5vw;
`;

const toggleTab = css`
`;

const offerStyleHide = css`
	bottom: 0vh;
`;

const offerBodyStyle = css`
	display:flex;
	place-content: space-between;
	background: url(${backgroundTextureMarket});
	background-color: ${Colors.UIMarketBackground};
	padding: .5vw;
	min-height: 10vh;
	border-top-right-radius: .5vw;
	border-bottom-left-radius: .5vw;
	border-bottom-right-radius: .5vw;
`;

const offerBodyStyleHide = css`
	display: none;
`;

const offerAntibodyContainer = css`
	display:flex;
	position: absolute;
	left: 50%;
	height: 10vh;
	transform: translate(-50%, 0);
`;

const smallButtonCenterLeft = css`
	align-self: center;
	padding-right: 1vw;
`;

const smallButtonCenterRight = css`
	align-self: center;
	padding-left: 1vw;
`;

interface MarketProps {
	game: GameClient,
	experience: number,
	currentFunds: number,
	maxFunds: number,
	level: number,
	gainedMoney: number,
	offer?: Array<GameElement>,
	rewardOffer?: Array<GameElement>,
	setPurchased: (purchased: boolean) => void,
	hoverAntibody: (antibodyType: GameElement, index: number) => void;
	clearHoverAntibody: () => void;
	onShowMarketButton: (show: boolean) => void;
	full: boolean,
	numCells: number,
	capacity: number,
	sendWarning: (name: string) => void;
	sendInfo: (name: string) => void;
	currentState: string;
	draggedCell: boolean;
	page: number;
	prevPage: () => void;
	nextPage: () => void;
	setPage: (page: number) => void;
	enabledButtons?: string;
	tooltips?: React.ReactNode;
};

declare var gameClient: GameClient;
let immuneCellButtonAnimationTimeout: any = null;

export const Market = ({
	game,
	offer,
	rewardOffer = [],
	currentFunds,
	maxFunds,
	experience,
	level,
	setPurchased,
	hoverAntibody,
	clearHoverAntibody,
	onShowMarketButton,
	full,
	gainedMoney,
	numCells,
	capacity,
	sendWarning,
	sendInfo,
	currentState,
	draggedCell,
	page,
	prevPage,
	nextPage,
	setPage,
	enabledButtons,
	tooltips
}: MarketProps) => {
	let [showUI, setShowUI] = React.useState(true);
	const [temporaryHide, setTempHide] = React.useState(false);
	const [locked, setLocked] = React.useState(false);
	const [shakeGold, setShakeGold] = React.useState(false);
	const [flashWarning, setFlashWarning] = React.useState(false);
	const temporaryHideRef = React.useRef(temporaryHide);
	const [h, setH] = React.useState(false);
	const toggleHide = () => {
		setH(!h);
		gameClient.setHideMarket(h);
		onShowMarketButton(!gameClient.hideMarket);
	}
	const [hovered, setHovered] = React.useState(0);
	const [shouldAnimate, setShouldAnimate] = React.useState(false);

	const marketRef = React.useRef(null);

	React.useEffect(() => {
		const animation = document.getElementById("market");
		animation?.addEventListener('transitionstart', () => {
			if (game.hideMarket || temporaryHideRef.current) {
				setShowUI(false);
			}
		});
		animation?.addEventListener('transitionend', () => {
			if (!(game.hideMarket && temporaryHideRef.current)) {
				setShowUI(true);
			}
		});


		const listener = () => {
			temporaryHideRef.current = false;
			setTempHide(temporaryHideRef.current);
			//setPurchased(true);
			setHovered(0);
		};
		window.addEventListener("mouseup", listener);

		() => {
			window.removeEventListener("mouseup", listener);
		}
	}, []);

	const duringSim = game.getCurrentState() === "simulation";
	const hide = game.hideMarket || temporaryHideRef.current || duringSim;
	const player = game.getPlayer();
	const toggleLockShop = () => {
		setLocked(!locked);
		game.sendFreezeShop();
	}

	const hover = () => {
		if (gameClient) {
			gameClient.playUISound(Sound.Menu);
		}
	}

	const onMouse: MouseEventHandler<HTMLDivElement> = (e) => {
		//When the mouse is active over the market let the placement system know to
		//prevent interaction with entities under the market
		if (game.hideMarket) return;
		const placement = getSystem(gameClient.world, PlacementSystem);
		placement.signalUIInterceptThisFrame();
	};

	useEffect(() => {
		if (game.cameraSystem.getZoomLevel() === CameraZoomLevel.Normal) {
			game.setHideMarket(game.isCurrentlyDragging);
		}

	}, [game.isCurrentlyDragging]);

	useEffect(() => {
		if (gameClient.rerollNotication) {
			// Force animation to reset and restart by briefly setting to false then true
			setShouldAnimate(false);
			if (immuneCellButtonAnimationTimeout !== null) {
				clearTimeout(immuneCellButtonAnimationTimeout);
			}
			gameClient.rerollNotication = false;

			// Use requestAnimationFrame to ensure the state change is processed
			requestAnimationFrame(() => {
				setShouldAnimate(true);

				// Reset animation state after animation completes
				// Animation duration is 0.2s + (max_index * 0.1s delay)
				// Using 0.8s as a safe timeout to ensure all animations complete
				immuneCellButtonAnimationTimeout = setTimeout(() => {
					setShouldAnimate(false);
					immuneCellButtonAnimationTimeout = null;
				}, 800);
			});
		}


	}, [gameClient.rerollNotication]);

	//const items = offer?.concat(rewardOffer || []) || [];
	let items = new Array<{ isReward: boolean, el: GameElement, idx: number }>();
	if (offer) {
		offer.forEach((el, idx) => {
			items.push({ isReward: false, el: el, idx });
		});
	}
	rewardOffer.forEach((el, idx) => {
		items.push({ isReward: true, el: el, idx });
	});


	const renderItems = () => {
		let indexOfLastItem = page * ShopConstants.CellsPerPage;
		let indexOfFirstItem = indexOfLastItem - ShopConstants.CellsPerPage;
		let currentItems = items.slice(indexOfFirstItem, indexOfLastItem);
		if (currentItems.length === 0) {
			setPage(1);
			currentItems = items.slice(0, ShopConstants.CellsPerPage);
		}

		return coloredSpritesheet &&
			currentItems?.map(
				({ isReward, idx, el: immuneCell }, index) => {
					let flash = false;
					let flashColors: [string, string] = ['', ''];
					if (isReward) {
						flash = true;
						flashColors = ['lawngreen', 'lightgreen'];
					} else if (player) {
						const elements = findAlikeElements(player, immuneCell);
						if (canGroupPromote(elements, 1)) {
							flash = true;
							flashColors = ['lightgoldenrodyellow', 'gold'];
						}
					}

					const isEnabled = enabledButtons === undefined || enabledButtons === 'immuneCell';

					const rewardTier = getRewardCellTier(immuneCell);
					const isRewardType = rewardTier > 0;

					return (<AntibodyButton
						index={idx}
						isReward={isReward}
						antibodyType={immuneCell}
						flash={flash}
						flashColors={flashColors}
						setPurchased={setPurchased}
						setHovered={setHovered}
						hoverAntibody={hoverAntibody}
						clearHoverAntibody={clearHoverAntibody}
						shakeGold={setShakeGold}
						flashWarning={setFlashWarning}
						tier={isRewardType ? rewardTier : getImmuneCellTier(immuneCell)}
						isRewardTier={isRewardType}
						key={`market_${index}`}
						toggleHide={() => {
							gameClient.setHideMarket(true);
							temporaryHideRef.current = true;
							setTempHide(temporaryHideRef.current);
						}}
						sendWarning={sendWarning}
						enabled={isEnabled}
						isLocked={locked}
						shouldAnimate={shouldAnimate}
					/>
					)
				}
			)
	}

	return (
		<div id="market"
			ref={marketRef}
			css={css`${offerStyle} ${hide && offerStyleHide}`}
			//Prevent focus of anything on the market so we don't
			//get accidental spacebar clicks
			onFocus={(e) => (e.target as HTMLElement).blur()}
		>
			<div css={tooltipAnchor}>{tooltips}</div>
			<div css={offerTab} onClick={toggleHide}>
				<div css={toggleTab} onMouseEnter={hover}>
					<FontAwesomeIcon icon={hide ? faChevronDown : faChevronUp} />
				</div>
				<GoldCounter shake={shakeGold} setShake={setShakeGold} count={currentFunds} max={Math.max(10, currentFunds)} hovered={hovered} justGained={gainedMoney} />
			</div>
			<span className="market-body" css={css`${offerBodyStyle} ${hide && offerBodyStyleHide}`}
				onMouseDown={onMouse}
				onMouseUp={onMouse}
				onMouseMove={onMouse}
			>
				<span css={css`display: flex; gap: .25vw; align-self: center;`}>
					<TierMarker tier={level} position="top-left" relative={true} displayNumber={true} />
					<ExperienceButton
						level={level}
						experience={experience}
						setPurchased={setPurchased}
						setHovered={setHovered}
						sendInfo={sendInfo}
						enabledButtons={enabledButtons}
					/>
					<RefreshButton
						setHovered={setHovered}
						sendInfo={sendInfo}
						enabled={enabledButtons === undefined || enabledButtons === 'refresh'}
					/>
					<FreezeButton
						toggleLockShop={toggleLockShop}
						sendInfo={sendInfo}
						enabled={enabledButtons === undefined || enabledButtons === 'freeze'}
					/>
					<FontAwesomeIcon icon={locked ? faLock : faLockOpen} />
				</span>
				{!hide && showUI && <>
					<div css={offerAntibodyContainer}>
						{items.length > ShopConstants.CellsPerPage && <div css={smallButtonCenterLeft} onClick={prevPage}><FontAwesomeIcon size={'2x'} icon={faArrowCircleLeft} /></div>}
						{renderItems()}
						{items.length > ShopConstants.CellsPerPage && <div css={smallButtonCenterRight} onClick={nextPage}><FontAwesomeIcon size={'2x'} icon={faArrowCircleRight} /></div>}
					</div>
					<span></span> {/*Stand in for the button span above so that place-content: space-between does the right thing */}
					{/* <CapacityDisplay numCells={numCells} capacity={capacity} /> */}
					<NextRoundButton enabled={!gameClient.playerHasEndedTurn && (enabledButtons === undefined || enabledButtons === 'nextRound')} funds={currentFunds} sendInfo={sendInfo} currentState={currentState} />
				</>}
			</span>
			<CapacityPanel
				flashWarning={flashWarning}
				hide={hide || !!draggedCell}
				numCells={numCells}
				capacity={capacity}
				sendInfo={sendInfo}
			/>
		</div>
	)
};
